import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BackToDashboard,
  PricingHeaderInner,
  PricingStyled,
  PricingTitle,
  PricingWrapper,
  RightSideWrapper,
  TabsWrapper,
  TopRow,
} from 'Features/pricing/routes/Pricing.styled'

import {
  AdClick,
  GeneralSelect,
  Globe,
  Stack,
  TabsPricing,
  useResponsive,
} from '@clickcease/cc-client-components-lib'
import { Tab as TabType } from 'Types/Tab'

import { PaidMarketingTab } from 'Features/pricing/tabs/PaidMarketing/PaidMarketing'
import { useGetPlans } from 'Features/pricing/api/getPlans'
import { PricingFooter } from '../components/PricingFooter/PricingFooter'
import { BotMitigation } from 'Features/pricing/tabs/BotMitigation/BotMitigation'
import { Bundle } from 'Features/pricing/tabs/Bundles/Bundles'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { PlanTypesSorted } from 'Features/pricing/types/PlanTypesSorted'
import { useGetPlan } from 'Features/pricing/hooks/useGetPlan'
import usePricingStore from '../stores/pricing'
import { LeftCornerLogo } from 'Components/Elements/CheqLogo/LeftCornerLogo'
import languages from 'Assets/lists/languages'
import { LANGUAGES_OPTIONS, LanguagesTypes } from 'Types/Languages'

import { useUserStore } from 'Stores/userStoreModuleFed'
import { useUpdateUserProfile } from 'Features/userProfile/api/updateUserProfile'
import { useSetLanguage } from 'Hooks/useSetLanguage'
import { SwitchAccounts } from '../components/SwitchAccounts/SwitchAccounts'
import { useGetUserAccounts } from 'Features/AccountSelector/api/getUserAccounts'
import { useVerifyUrlCodeFromParams } from 'Features/auth/hooks/useVerifyUrlCodeFromParams'
import { usePricingEndOfTrial } from 'Hooks/split/usePricingEndOfTrial'
import { usePaymentsFlowSplit } from 'Hooks/split/usePaymentsFlow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faArrowLeft } from '@fortawesome/pro-thin-svg-icons'

type locationStateType = {
  selectedTab: number
  reactivation?: boolean
  billingCycleMonths?: number
}

export const Pricing = () => {
  const { isMobile } = useResponsive()
  const { t } = useTranslation('pricing')
  const plans = useGetPlans()
  const { getSessionStorageItem } = useVerifyUrlCodeFromParams()
  const [plansByType, setPlansByType] = useState<PlanTypesSorted>({} as PlanTypesSorted)
  const location = useLocation()
  const [showLanguageSelector, setShowLanguageSelector] = useState<boolean>(false)
  const locationState = location.state as locationStateType
  const [queryParameters] = useSearchParams()
  const { sortPlansByType } = useGetPlan()
  const { setLanguage: setUserLanguage } = useSetLanguage()
  const { language, multiAccount } = useUserStore()
  const { mutate: updateLanguageInDb } = useUpdateUserProfile()
  const { data: accounts } = useGetUserAccounts()
  const { setChosenTab, chosenTab } = usePricingStore()
  const isMultiAccount = multiAccount || accounts?.accounts.length > 1
  const partner = getSessionStorageItem('partner')
  const navigate = useNavigate()
  const pricingEndOfTrial = usePricingEndOfTrial()
  const paymentFlow = usePaymentsFlowSplit()
  const shouldDisplayBackButton = paymentFlow === 'NO_CC' && pricingEndOfTrial && !partner

  useEffect(() => {
    setShowLanguageSelector(queryParameters.get('showSelector') === 'true')
  }, [queryParameters])

  useEffect(() => {
    setPlansByType(sortPlansByType(plans.data || []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans.data])

  const changeLanguage = (languageParam: LanguagesTypes) => {
    updateLanguageInDb(
      { type: 'language', value: languageParam },
      {
        onSuccess: async () => {
          await setUserLanguage(languageParam || LANGUAGES_OPTIONS.EN)
        },
      }
    )
  }

  const tabs = [
    {
      id: 0,
      title: t('paid_marketing_title'),
      pageName: 'paidMarketing',
      icon: isMobile ? undefined : <AdClick />,
      page: (
        <PaidMarketingTab
          plansDb={plansByType.paidMarketing || []}
          partner={!!partner}
          reactivation={locationState?.reactivation}
          billingCycleMonths={locationState?.billingCycleMonths}
        />
      ),
    },
    {
      id: 1,
      title: t('bot_mitigation_title'),
      pageName: 'botMitigation',
      icon: isMobile ? undefined : <Globe />,
      page: (
        <BotMitigation
          plansDb={plansByType.botMitigation || []}
          partner={!!partner}
          reactivation={locationState?.reactivation}
          billingCycleMonths={locationState?.billingCycleMonths}
        />
      ),
    },
    {
      id: 2,
      title: t('bundles_title'),
      pageName: 'bundle',
      icon: isMobile ? undefined : <Stack />,
      page: (
        <Bundle
          paidMarketingPlans={plansByType.paidMarketing || []}
          botMitigationPlans={plansByType.botMitigation || []}
          reactivation={locationState?.reactivation}
          billingCycleMonths={locationState?.billingCycleMonths}
        />
      ),
    },
  ]

  const [activeTab, setActiveTab] = useState<TabType>(() => {
    return tabs[locationState?.selectedTab || chosenTab || 0]
  })

  useEffect(() => {
    setChosenTab(activeTab.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const getTitle = () => {
    if (locationState?.reactivation) {
      return 'reactivation_title'
    } else if (isMobile) {
      return 'mobile-title'
    } else {
      return 'title'
    }
  }

  return (
    <PricingStyled>
      <PricingWrapper>
        <TopRow>
          <LeftCornerLogo />
          <RightSideWrapper>
            {showLanguageSelector && (
              <GeneralSelect
                value={languages.find((item) => item.value === language)}
                wrapperCSS={{
                  margin: '12px 30px 0px 0px',
                  width: '100px',
                  '> div:nth-child(1) > div': {
                    minWidth: '120px',
                  },
                }}
                options={languages}
                onChange={(item) => {
                  changeLanguage((item?.value as LanguagesTypes) || LANGUAGES_OPTIONS.EN)
                }}
              />
            )}
            <SwitchAccounts isHidden={!isMultiAccount} />
            {shouldDisplayBackButton && (
              <BackToDashboard
                onClick={() => {
                  navigate('/')
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft as IconProp} />
                {t('back_to_dashboard')}
              </BackToDashboard>
            )}
          </RightSideWrapper>
        </TopRow>
        <PricingHeaderInner>
          <PricingTitle>{t(getTitle())}</PricingTitle>
        </PricingHeaderInner>
        <TabsWrapper>
          <TabsPricing
            tabs={tabs}
            activeTab={activeTab}
            onClick={setActiveTab}
            css={{ padding: '0px $6' }}
          />
        </TabsWrapper>
        <PricingFooter bundle={activeTab.id === 2} />
      </PricingWrapper>
    </PricingStyled>
  )
}
